/**
 * Deep-link preservation across the SSO round trip.
 *
 * A protected route hands the browser to the IdP; the backend redirects back to
 * `/auth/callback`, which is a FULL page load — React state is gone by then, so
 * the intended path is parked in sessionStorage rather than memory or a route
 * state object. sessionStorage is per-tab and dies with it, which is exactly the
 * lifetime a pending sign-in should have.
 */

const RETURN_TO_KEY = 'awards.returnTo';
const ATTEMPT_KEY = 'awards.signInAttemptAt';

/**
 * How long a sign-in may take before we stop believing it is in flight. Long
 * enough for a real IdP round trip including a password and a 2FA prompt.
 */
const ATTEMPT_WINDOW_MS = 2 * 60 * 1000;

/**
 * Only same-origin, absolute-path targets survive. Anything else — an absolute
 * URL, a protocol-relative `//evil.com`, a path that does not start with `/` —
 * is discarded and the caller falls back to a default.
 *
 * This is the open-redirect guard: `returnTo` decides where a freshly
 * authenticated browser lands, so treating it as untrusted input is the whole
 * point. Never relax this to accept a full URL.
 */
export function isSafeReturnTo(path: string | null | undefined): path is string {
  if (!path) return false;
  if (!path.startsWith('/')) return false;
  // `//host` and `/\host` are protocol-relative URLs, not local paths.
  if (path.startsWith('//') || path.startsWith('/\\')) return false;
  return true;
}

/** Park the path the user was trying to reach, just before leaving for the IdP. */
export function rememberReturnTo(path: string): void {
  try {
    if (isSafeReturnTo(path)) sessionStorage.setItem(RETURN_TO_KEY, path);
  } catch {
    /* private mode / storage disabled — we simply lose the deep link */
  }
}

/** Read and clear the parked path. Returns null when there is nothing safe to use. */
export function takeReturnTo(): string | null {
  try {
    const value = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    return isSafeReturnTo(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Record that we are handing the browser to the IdP.
 *
 * This is the loop breaker. Without it, an IdP that returns the user still
 * unauthenticated — they cancelled, or the session was rejected — lands on a
 * protected route, which redirects to the IdP, forever. Marking the attempt lets
 * the next unauthenticated render tell "first arrival" apart from "we already
 * tried and it did not work".
 */
export function markSignInAttempt(): void {
  try {
    sessionStorage.setItem(ATTEMPT_KEY, String(Date.now()));
  } catch {
    /* storage disabled: we lose loop protection, not correctness */
  }
}

/** True when a sign-in was started recently and has not yet succeeded. */
export function signInAttemptPending(): boolean {
  try {
    const raw = sessionStorage.getItem(ATTEMPT_KEY);
    if (!raw) return false;
    const startedAt = Number(raw);
    if (!Number.isFinite(startedAt)) return false;
    if (Date.now() - startedAt > ATTEMPT_WINDOW_MS) {
      // Stale: an abandoned attempt should not block a fresh one forever.
      clearSignInAttempt();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Clear the attempt marker — on success, or when the user retries deliberately. */
export function clearSignInAttempt(): void {
  try {
    sessionStorage.removeItem(ATTEMPT_KEY);
  } catch {
    /* nothing to clear */
  }
}
